/**
 * Type definitions for SSE stream events from the Karl AI service.
 * Provides type safety for event processing and payload handling.
 */

/**
 * Represents a payload from a Karl custom event
 */
export interface KarlCustomPayload {
  /** Event type identifier */
  type?: string;
  /** Event title (often used for tool names) */
  title?: string;
  /** Event content (varies by event type) */
  content?: unknown;
  /** Event ID for tracking */
  id?: string | number;
  /** Node name in the workflow */
  node?: string;
  /** Tool run ID for tracking streaming ownership */
  tool_run_id?: string;
}

/**
 * Type guard to check if a value is a KarlCustomPayload
 */
export function isKarlCustomPayload(value: unknown): value is KarlCustomPayload {
  return typeof value === "object" && value !== null;
}

/**
 * Represents metadata from a finalized answer
 */
export interface AnswerMetadata {
  /** Array of source documents */
  sources?: Array<{
    /** Document ID */
    id: string;
    /** Document title */
    title: string;
    /** Relevant excerpt */
    excerpt?: string;
    /** Page number */
    page?: number;
  }>;
  /** Additional metadata fields */
  [key: string]: unknown;
}

/**
 * Represents a finalized answer with content and metadata
 */
export interface FinalizedAnswer {
  /** The answer content */
  content?: string;
  /** Metadata including sources */
  metadata?: AnswerMetadata | string;
}

/**
 * Type guard to check if content has metadata property
 */
export function hasMetadata(obj: unknown): obj is { metadata: unknown } {
  return typeof obj === "object" && obj !== null && "metadata" in obj;
}

/**
 * Represents an error event from the backend
 */
export interface BackendErrorPayload {
  /** Error message */
  message?: string;
  /** Error code */
  code?: string;
}

/**
 * Type guard for backend error payload
 */
export function isBackendErrorPayload(value: unknown): value is BackendErrorPayload {
  return (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    typeof (value as BackendErrorPayload).message === "string"
  );
}
